import {
  connectorProviderApiBaseUrls,
  customWebhookConnectorType,
  facebookCommentsConnectorType,
  facebookMessengerConnectorType,
  gmailConnectorType,
  googleConnectorProvider,
  gupshupConnectorProvider,
  instagramConnectorType,
  instagramCommentsConnectorType,
  nativeDeviceConnectorType,
  providerWhatsAppConnectorType,
  telegramBotConnectionVariant,
  telegramBusinessConnectionVariant,
  telegramConnectorType,
  type TelegramConnectionVariant,
  tiktokBusinessConnectorType,
} from "./connector-constants";

import type {
  ContactCenterChannel,
  ContactCenterChannelType,
  ContactCenterConnector,
  ContactCenterConnectorDraft,
  ContactCenterConnectorProvider,
  ContactCenterConnectorSettings,
  ContactCenterConnectorType,
  ContactCenterProviderSecretDraft,
  ContactCenterHistorySyncResponse,
  ContactCenterHistorySyncStatus,
} from "@/src/features/contact-center/types";
import type {
  ContactCenterConnectorInstructions,
  ContactCenterConnectorInstructionsContext,
} from "./registry";

export type ConnectorSettingsRecord = Record<string, unknown> | null;

export const connectorTypeValues = [
  nativeDeviceConnectorType,
  providerWhatsAppConnectorType,
  facebookMessengerConnectorType,
  facebookCommentsConnectorType,
  instagramConnectorType,
  instagramCommentsConnectorType,
  tiktokBusinessConnectorType,
  gmailConnectorType,
  telegramConnectorType,
  customWebhookConnectorType,
] as const;

export type ConnectorOptionLike = {
  connectorType?: ContactCenterConnectorSettings["connectorType"];
  provider?: ContactCenterConnectorProvider;
};

export type ConnectorInstructionsOptionLike = {
  instructions?:
    | ContactCenterConnectorInstructions
    | ((
        context: ContactCenterConnectorInstructionsContext,
      ) => ContactCenterConnectorInstructions);
};

export function connectorHasSetupInstructions(
  connectorType: ConnectorInstructionsOptionLike | undefined,
): boolean {
  return Boolean(connectorType?.instructions);
}

export function connectorSetupInstructions(
  connectorType: ConnectorInstructionsOptionLike | undefined,
  settings: Pick<ContactCenterConnectorSettings, "telegramConnectionVariant">,
): ContactCenterConnectorInstructions | null {
  if (!connectorType?.instructions) {
    return null;
  }

  if (typeof connectorType.instructions === "function") {
    return connectorType.instructions({
      telegramConnectionVariant: telegramConnectionVariant(
        settings.telegramConnectionVariant,
      ),
    });
  }

  return connectorType.instructions;
}

export function connectorOptionDefaultSecret(
  connectorType?: ConnectorOptionLike,
): ContactCenterProviderSecretDraft {
  if (connectorType?.provider === gupshupConnectorProvider) {
    return { whatsappApiKey: "", webhookSecret: "", apiKey: "" };
  }
  if (connectorType?.connectorType === tiktokBusinessConnectorType) {
    return { whatsappApiKey: "", webhookSecret: "", appSecret: "" };
  }
  if (connectorType?.connectorType === telegramConnectorType) {
    return { whatsappApiKey: "", webhookSecret: "", botToken: "" };
  }
  return { whatsappApiKey: "", webhookSecret: "" };
}

export function connectorOptionRequiresAppConfiguration(
  connectorType: ConnectorOptionLike | undefined,
): boolean {
  return connectorType?.provider === gupshupConnectorProvider;
}

export function connectorOptionRequiresDeviceConfiguration(
  connectorType: ConnectorOptionLike | undefined,
): boolean {
  return connectorType?.connectorType === nativeDeviceConnectorType;
}

export function connectorProviderFallbackForApiBaseUrl(
  connectorType: ConnectorOptionLike | undefined,
): ContactCenterConnectorProvider {
  return connectorType?.provider ?? gupshupConnectorProvider;
}

export function connectorWebhookTokenValidationMessage(): string {
  return "Webhook verify token is required before saving.";
}

export function connectorApiSecretValidationMessageForDraft(
  settings: ContactCenterConnectorSettings,
): string {
  return connectorApiSecretValidationMessage(settings);
}

export function connectorTypeFromSettings(
  value: unknown,
): ContactCenterConnectorType | undefined {
  return connectorTypeValues.includes(
    value as (typeof connectorTypeValues)[number],
  )
    ? (value as ContactCenterConnectorType)
    : undefined;
}

export function connectorTypeForNativeDevice(): ContactCenterConnectorType {
  return nativeDeviceConnectorType;
}

export function connectorTypeForProviderMessaging(): ContactCenterConnectorType {
  return providerWhatsAppConnectorType;
}

export function defaultConnectorSettings(): ContactCenterConnectorSettings {
  return {
    autoCreateContact: true,
    updateExistingContact: true,
    placeholderContactsAllowed: true,
    autoCreateDeal: false,
    createDealMode: "FIRST_CONVERSATION",
    assignmentMode: "NONE",
    pickupMode: "ANY_ELIGIBLE_EMPLOYEE",
    defaultPipelineId: null,
    defaultStageId: null,
    defaultOwnerTenantMembershipId: null,
    defaultAssigneeTenantMembershipId: null,
    inboundEnabled: true,
    outboundEnabled: true,
    connectorType: nativeDeviceConnectorType,
    connectorProvider: "META",
    handleId: "",
    contactAddress: "",
    deviceDisplayName: "",
    appName: "",
    source: "",
    apiBaseUrl: "",
  };
}

export function providerApiBaseUrl(
  connectorProvider: ContactCenterConnectorProvider,
): string {
  return (
    (connectorProviderApiBaseUrls as Record<string, string>)[
      connectorProvider
    ] ?? ""
  );
}

export function settingsConnectorProvider(
  settings: ConnectorSettingsRecord,
): ContactCenterConnectorProvider {
  const connectorProvider = settings?.connectorProvider;
  const legacyProvider = settings?.provider;
  if (connectorProvider === googleConnectorProvider) {
    return googleConnectorProvider;
  }

  if (connectorProvider === "GUPSHUP" || connectorProvider === "META") {
    return connectorProvider === "META" && typeof legacyProvider === "string"
      ? (legacyProvider as ContactCenterConnectorProvider)
      : connectorProvider;
  }

  return typeof legacyProvider === "string"
    ? (legacyProvider as ContactCenterConnectorProvider)
    : "META";
}

export function connectorSettingsFromRecord(
  settings: ConnectorSettingsRecord,
  base: Omit<
    ContactCenterConnectorSettings,
    | "connectorType"
    | "connectorProvider"
    | "handleId"
    | "contactAddress"
    | "deviceDisplayName"
    | "appName"
    | "source"
    | "apiBaseUrl"
    | "inboundEnabled"
    | "outboundEnabled"
    | "pickupMode"
  >,
  channelType?: ContactCenterChannelType,
): ContactCenterConnectorSettings {
  const connectorType =
    connectorTypeFromSettings(settings?.connectorType) ??
    defaultConnectorTypeForChannel(channelType);
  const connectorProvider = settingsConnectorProvider(settings);
  return {
    ...base,
    pickupMode: pickupModeSetting(settings),
    inboundEnabled: booleanSetting(settings, "inboundEnabled", true),
    outboundEnabled: booleanSetting(
      settings,
      "outboundEnabled",
      connectorType !== customWebhookConnectorType,
    ),
    connectorType,
    connectorProvider,
    handleId: stringSetting(settings, "handleId") ?? "",
    contactAddress:
      stringSetting(settings, "whatsappUserId") ??
      stringSetting(settings, "phoneNumberId") ??
      "",
    deviceDisplayName: stringSetting(settings, "deviceDisplayName") ?? "",
    appName: stringSetting(settings, "appName") ?? "",
    source: stringSetting(settings, "source") ?? "",
    apiBaseUrl:
      stringSetting(settings, "apiBaseUrl") ??
      providerApiBaseUrl(connectorProvider),
    pageId: stringSetting(settings, "pageId"),
    pageName: stringSetting(settings, "pageName"),
    instagramAccountId: stringSetting(settings, "instagramAccountId"),
    instagramUsername: stringSetting(settings, "instagramUsername"),
    oauthProvider: oauthProviderSetting(settings),
    oauthConnectedAt: stringSetting(settings, "oauthConnectedAt"),
    graphApiVersion: stringSetting(settings, "graphApiVersion"),
    appId: stringSetting(settings, "appId"),
    appSecret: stringSetting(settings, "appSecret"),
    authorizationUrl: stringSetting(settings, "authorizationUrl"),
    webhookUrl: stringSetting(settings, "webhookUrl"),
    mailboxEmail: stringSetting(settings, "mailboxEmail"),
    mailboxDisplayName: stringSetting(settings, "mailboxDisplayName"),
    defaultEmailFooter: stringSetting(settings, "defaultEmailFooter") ?? "",
    googleUserId: stringSetting(settings, "googleUserId"),
    historyId: stringSetting(settings, "historyId"),
    watchExpiration: stringSetting(settings, "watchExpiration"),
    pubsubTopicName: stringSetting(settings, "pubsubTopicName"),
    businessId: stringSetting(settings, "businessId"),
    callbackFlag: booleanSetting(settings, "callbackFlag", true),
    tokenExpiresAt: stringSetting(settings, "tokenExpiresAt"),
    refreshTokenExpiresAt: stringSetting(settings, "refreshTokenExpiresAt"),
    signatureValidationEnabled: booleanSetting(
      settings,
      "signatureValidationEnabled",
      true,
    ),
    subscribedFields: stringArraySetting(settings, "subscribedFields"),
    telegramConnectionVariant: telegramConnectionVariantSetting(settings),
    telegramBusinessConnection: telegramBusinessConnectionSetting(settings),
    telegramBotUsername: stringSetting(settings, "telegramBotUsername"),
    telegramBusinessConnectionId: stringSetting(
      settings,
      "telegramBusinessConnectionId",
    ),
    telegramBusinessAccountName: stringSetting(
      settings,
      "telegramBusinessAccountName",
    ),
    telegramBusinessConnectedAt: stringSetting(
      settings,
      "telegramBusinessConnectedAt",
    ),
  };
}

export function connectorRequestSettings(
  settings: ContactCenterConnectorSettings,
): Record<string, unknown> {
  if (settings.connectorType === customWebhookConnectorType) {
    return {
      inboundEnabled: settings.inboundEnabled,
      outboundEnabled: settings.outboundEnabled,
      pickupMode: settings.pickupMode,
      connectorType: customWebhookConnectorType,
    };
  }

  if (
    settings.connectorType === facebookMessengerConnectorType ||
    settings.connectorType === facebookCommentsConnectorType ||
    settings.connectorType === instagramConnectorType ||
    settings.connectorType === instagramCommentsConnectorType
  ) {
    return compactSettings({
      inboundEnabled: settings.inboundEnabled,
      outboundEnabled: settings.outboundEnabled,
      pickupMode: settings.pickupMode,
      connectorType: settings.connectorType,
      connectorProvider: "META",
      apiBaseUrl: settings.apiBaseUrl,
      pageId: settings.pageId,
      pageName: settings.pageName,
      instagramAccountId: settings.instagramAccountId,
      instagramUsername: settings.instagramUsername,
      oauthProvider: settings.oauthProvider,
      oauthConnectedAt: settings.oauthConnectedAt,
      graphApiVersion: settings.graphApiVersion,
      signatureValidationEnabled: settings.signatureValidationEnabled,
      subscribedFields: settings.subscribedFields,
    });
  }

  if (settings.connectorType === tiktokBusinessConnectorType) {
    return compactSettings({
      inboundEnabled: settings.inboundEnabled,
      outboundEnabled: settings.outboundEnabled,
      pickupMode: settings.pickupMode,
      connectorType: settings.connectorType,
      appId: settings.appId,
      appSecret: settings.appSecret,
      authorizationUrl: settings.authorizationUrl,
      webhookUrl: settings.webhookUrl,
      businessId: settings.businessId,
      callbackFlag: settings.callbackFlag,
      oauthConnectedAt: settings.oauthConnectedAt,
      tokenExpiresAt: settings.tokenExpiresAt,
      refreshTokenExpiresAt: settings.refreshTokenExpiresAt,
      signatureValidationEnabled: settings.signatureValidationEnabled,
    });
  }

  if (settings.connectorType === gmailConnectorType) {
    return compactSettings({
      inboundEnabled: settings.inboundEnabled,
      outboundEnabled: settings.outboundEnabled,
      pickupMode: settings.pickupMode,
      connectorType: gmailConnectorType,
      connectorProvider: googleConnectorProvider,
      mailboxEmail: settings.mailboxEmail,
      mailboxDisplayName: settings.mailboxDisplayName,
      defaultEmailFooter: settings.defaultEmailFooter,
      googleUserId: settings.googleUserId,
      historyId: settings.historyId,
      watchExpiration: settings.watchExpiration,
      pubsubTopicName: settings.pubsubTopicName,
      oauthConnectedAt: settings.oauthConnectedAt,
      tokenExpiresAt: settings.tokenExpiresAt,
      refreshTokenExpiresAt: settings.refreshTokenExpiresAt,
    });
  }

  if (settings.connectorType === telegramConnectorType) {
    return compactSettings({
      inboundEnabled: settings.inboundEnabled,
      outboundEnabled: settings.outboundEnabled,
      pickupMode: settings.pickupMode,
      connectorType: telegramConnectorType,
      telegramConnectionVariant: telegramConnectionVariant(
        settings.telegramConnectionVariant,
      ),
      telegramBotUsername: settings.telegramBotUsername,
      telegramBusinessConnectionId: settings.telegramBusinessConnectionId,
      telegramBusinessAccountName: settings.telegramBusinessAccountName,
      telegramBusinessConnectedAt: settings.telegramBusinessConnectedAt,
    });
  }

  const usesNativeConnector =
    settings.connectorType === nativeDeviceConnectorType;
  const connectorType = usesNativeConnector
    ? nativeDeviceConnectorType
    : providerWhatsAppConnectorType;
  const connectorProvider = settingsConnectorProvider(
    settings as unknown as ConnectorSettingsRecord,
  );
  const outboundEnabled = usesNativeConnector ? true : settings.outboundEnabled;

  if (usesNativeConnector) {
    return {
      inboundEnabled: settings.inboundEnabled,
      outboundEnabled,
      pickupMode: settings.pickupMode,
      connectorType,
      handleId: settings.handleId.trim(),
      whatsappUserId: (
        settings.contactAddress.trim() ||
        stringSetting(
          settings as unknown as ConnectorSettingsRecord,
          "whatsappUserId",
        ) ||
        ""
      ).trim(),
      deviceDisplayName: settings.deviceDisplayName.trim(),
      apiBaseUrl: settings.apiBaseUrl.trim(),
    };
  }

  if (connectorType !== providerWhatsAppConnectorType) {
    return {
      inboundEnabled: settings.inboundEnabled,
      outboundEnabled,
      pickupMode: settings.pickupMode,
      connectorType,
    };
  }

  return {
    inboundEnabled: settings.inboundEnabled,
    outboundEnabled,
    pickupMode: settings.pickupMode,
    connectorType,
    provider: connectorProvider,
    appName: settings.appName.trim(),
    appId: settings.appId?.trim(),
    source: settings.source.trim(),
    apiBaseUrl:
      settings.apiBaseUrl.trim() || providerApiBaseUrl(connectorProvider),
  };
}

export function connectorAppNameLabel(
  settings: ContactCenterConnectorSettings,
): string {
  return connectorUsesGeneratedWebhookSetup(settings)
    ? "Gupshup app name"
    : "App name";
}

export function connectorUsesGeneratedWebhookSetup(
  settings: ContactCenterConnectorSettings,
): boolean {
  return (
    settings.connectorType !== nativeDeviceConnectorType &&
    settingsConnectorProvider(
      settings as unknown as ConnectorSettingsRecord,
    ) === gupshupConnectorProvider
  );
}

export function connectorProviderSecret(
  draft: ContactCenterConnectorDraft,
): string | undefined {
  const connectorType = draft.settings.connectorType;
  if (connectorType === tiktokBusinessConnectorType) {
    const appSecret = draft.providerSecret.appSecret?.trim() ?? "";
    if (appSecret.length === 0) {
      return undefined;
    }
    return JSON.stringify({ appSecret });
  }
  if (connectorType === telegramConnectorType) {
    const botToken = draft.providerSecret.botToken?.trim() ?? "";
    if (botToken.length === 0) {
      return undefined;
    }
    return JSON.stringify({ botToken });
  }
  if (
    connectorType !== nativeDeviceConnectorType &&
    connectorType !== providerWhatsAppConnectorType
  ) {
    return undefined;
  }

  const connectorProvider = settingsConnectorProvider(
    draft.settings as unknown as ConnectorSettingsRecord,
  );
  const secretEntries =
    connectorType === providerWhatsAppConnectorType &&
    connectorProvider === gupshupConnectorProvider
      ? [["apiKey", draft.providerSecret.apiKey?.trim() ?? ""]]
      : [
          ["accessToken", draft.providerSecret.whatsappApiKey.trim()],
          ["appSecret", draft.providerSecret.webhookSecret.trim()],
          ["apiKey", draft.providerSecret.apiKey?.trim() ?? ""],
        ];
  const populatedSecretEntries = secretEntries.filter(
    ([, value]) => value.length > 0,
  );

  if (populatedSecretEntries.length === 0) {
    return undefined;
  }

  return JSON.stringify(Object.fromEntries(populatedSecretEntries));
}

export function historySyncStatusFromValue(
  value: unknown,
): ContactCenterHistorySyncStatus | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  const record = value as Record<string, unknown>;
  const status = normalizeHistorySyncState(record.status);
  return {
    ready: record.ready === true,
    status,
    message: typeof record.message === "string" ? record.message : "",
    lastSyncedAt:
      typeof record.lastSyncedAt === "string" ? record.lastSyncedAt : null,
    lastAttemptedAt:
      typeof record.lastAttemptedAt === "string"
        ? record.lastAttemptedAt
        : null,
    lastErrorCode:
      typeof record.lastErrorCode === "string" ? record.lastErrorCode : null,
    importedCount:
      typeof record.importedCount === "number" ? record.importedCount : 0,
    skippedCount:
      typeof record.skippedCount === "number" ? record.skippedCount : 0,
    syncedChatCount:
      typeof record.syncedChatCount === "number" ? record.syncedChatCount : 0,
  };
}

export function historySyncResponseFromValue(
  value: unknown,
  fallback: string,
): ContactCenterHistorySyncResponse {
  const record =
    value && typeof value === "object" && !Array.isArray(value)
      ? (value as Record<string, unknown>)
      : {};
  const status = historySyncStatusFromValue(record.status) ?? {
    ready: false,
    status: "FAILED",
    message: fallback,
    lastSyncedAt: null,
    lastAttemptedAt: null,
    lastErrorCode: null,
    importedCount: 0,
    skippedCount: 0,
    syncedChatCount: 0,
  };

  return {
    ok: record.ok === true,
    status,
    importedCount:
      typeof record.importedCount === "number" ? record.importedCount : 0,
    skippedCount:
      typeof record.skippedCount === "number" ? record.skippedCount : 0,
    syncedChatCount:
      typeof record.syncedChatCount === "number" ? record.syncedChatCount : 0,
    message:
      typeof record.message === "string" ? record.message : status.message,
  };
}

export function connectorSupportsHistorySync(
  connector:
    | ContactCenterConnector
    | Pick<ContactCenterConnectorSettings, "connectorType">
    | { connectorType: string | null }
    | null
    | undefined,
): boolean {
  if (!connector) return false;
  const connectorType =
    "settings" in connector
      ? connector.settings.connectorType
      : connector?.connectorType;
  return (
    connectorType === nativeDeviceConnectorType ||
    connectorType === gmailConnectorType
  );
}

export function connectorSupportsManualHistorySync(
  connector:
    | ContactCenterConnector
    | Pick<ContactCenterConnectorSettings, "connectorType">
    | { connectorType: string | null }
    | null
    | undefined,
): boolean {
  if (!connector) return false;
  const connectorType =
    "settings" in connector
      ? connector.settings.connectorType
      : connector?.connectorType;
  return connectorSupportsHistorySync(connector) && connectorType !== gmailConnectorType;
}

export function connectorNeedsDeviceConfiguration(
  settings: ContactCenterConnectorSettings,
): boolean {
  return settings.connectorType === nativeDeviceConnectorType;
}

export function connectorUsesAppConfiguration(
  settings: ContactCenterConnectorSettings,
): boolean {
  return (
    settingsConnectorProvider(
      settings as unknown as ConnectorSettingsRecord,
    ) === gupshupConnectorProvider
  );
}

export function connectorTypeRequiresApiSecret(
  settings: ContactCenterConnectorSettings,
): boolean {
  return (
    settings.connectorType === nativeDeviceConnectorType ||
    settings.connectorType === telegramConnectorType ||
    settingsConnectorProvider(
      settings as unknown as ConnectorSettingsRecord,
    ) === gupshupConnectorProvider
  );
}

export function connectorApiSecretLabel(
  settings: ContactCenterConnectorSettings,
): string {
  if (settings.connectorType === telegramConnectorType) {
    return "Telegram bot token";
  }
  return settingsConnectorProvider(
    settings as unknown as ConnectorSettingsRecord,
  ) === gupshupConnectorProvider
    ? "Gupshup API key"
    : "API key";
}

export function connectorApiSecretValidationMessage(
  settings: ContactCenterConnectorSettings,
): string {
  if (settings.connectorType === tiktokBusinessConnectorType) {
    return "TikTok Business app secret is required when outbound replies are enabled.";
  }
  if (settings.connectorType === telegramConnectorType) {
    return "Telegram bot token is required when inbound or outbound messaging is enabled.";
  }
  return settingsConnectorProvider(
    settings as unknown as ConnectorSettingsRecord,
  ) === gupshupConnectorProvider
    ? "Gupshup API key is required when outbound replies are enabled."
    : "API key is required when outbound replies are enabled.";
}

export function connectorAppConfigurationValidationMessage(): string {
  return "App name and source contact address are required before saving.";
}

export function connectorDeletionDialogTitle(): string {
  return "Delete message?";
}

export function contactCenterConnectorDeliveryReadiness(
  connector: ContactCenterConnector,
): { ready: boolean; message: string } {
  if (!connector.settings.outboundEnabled) {
    return { ready: true, message: "Outbound disabled" };
  }

  if (connectorUsesMetaOAuth(connector.settings)) {
    return {
      ready: connector.providerSecretStatus.configured,
      message: connector.providerSecretStatus.configured
        ? "Meta OAuth connected"
        : "OAuth setup required.",
    };
  }

  if (connector.settings.connectorType === tiktokBusinessConnectorType) {
    return {
      ready: connector.providerSecretStatus.apiKeyConfigured,
      message: connector.providerSecretStatus.apiKeyConfigured
        ? "TikTok Business app credentials configured"
        : "OAuth setup required: missing TikTok Business app credentials.",
    };
  }

  if (connector.settings.connectorType === gmailConnectorType) {
    const mailbox = connector.settings.mailboxEmail?.trim();
    return {
      ready: connector.providerSecretStatus.configured,
      message: connector.providerSecretStatus.configured
        ? `Gmail OAuth connected${mailbox ? ` for ${mailbox}` : ""}`
        : "OAuth setup required.",
    };
  }

  if (connector.settings.connectorType === telegramConnectorType) {
    return {
      ready:
        connector.providerSecretStatus.apiKeyConfigured ||
        connector.providerSecretStatus.configured,
      message:
        connector.providerSecretStatus.apiKeyConfigured ||
        connector.providerSecretStatus.configured
          ? "Telegram bot token configured"
          : "Outbound not ready: missing Telegram bot token.",
    };
  }

  if (!connectorSupportsHistorySync(connector)) {
    return {
      ready: connector.providerSecretStatus.apiKeyConfigured,
      message: connector.providerSecretStatus.apiKeyConfigured
        ? "Gupshup API key configured"
        : "Outbound not ready: missing Gupshup API key.",
    };
  }

  const missing: string[] = [];
  if (!connector.settings.apiBaseUrl.trim()) missing.push("API base URL");
  if (!connector.providerSecretStatus.configured) missing.push("API key");

  return missing.length > 0
    ? {
        ready: false,
        message: `Outbound not ready: missing ${missing.join("/")}.`,
      }
    : { ready: true, message: "Outbound ready" };
}

export function contactCenterConnectorHistoryVariant(
  connector: ContactCenterConnector,
): "outline" | "warning" | "success" | "secondary" {
  if (!connectorSupportsHistorySync(connector)) return "outline";
  if (
    !connector.historySync?.ready ||
    connector.historySync.status === "FAILED" ||
    connector.historySync.status === "NOT_READY"
  )
    return "warning";
  if (connector.historySync.status === "SUCCEEDED") return "success";
  return "secondary";
}

export function connectorChannelCapabilities(type: ContactCenterChannelType) {
  const supported =
    type === "WHATSAPP" ||
    type === "FACEBOOK_MESSENGER" ||
    type === "FACEBOOK_COMMENTS" ||
    type === "INSTAGRAM" ||
    type === "INSTAGRAM_COMMENTS" ||
    type === tiktokBusinessConnectorType ||
    type === "EMAIL" ||
    type === "VOICE" ||
    type === telegramConnectorType ||
    type === customWebhookConnectorType;
  const metaOAuth =
    type === "FACEBOOK_MESSENGER" ||
    type === "FACEBOOK_COMMENTS" ||
    type === "INSTAGRAM" ||
    type === "INSTAGRAM_COMMENTS";
  const tiktokBusiness = type === tiktokBusinessConnectorType;
  const email = type === "EMAIL";
  const telegram = type === telegramConnectorType;
  return {
    status: supported ? ("AVAILABLE" as const) : ("COMING_SOON" as const),
    supportsInbound: supported,
    supportsOutbound:
      type === "WHATSAPP" || metaOAuth || tiktokBusiness || email || telegram,
    supportsOAuth: metaOAuth || tiktokBusiness || email,
    supportsWebhookSecret:
      type === "WHATSAPP" ||
      type === customWebhookConnectorType ||
      metaOAuth ||
      tiktokBusiness ||
      telegram,
  };
}

export function customWebhookChannelType(): ContactCenterChannelType {
  return customWebhookConnectorType as ContactCenterChannelType;
}

export function connectorTypeOptionsForChannel(channel: ContactCenterChannel) {
  return channel.type === "WHATSAPP" ? "messaging" : "webhook";
}

export function connectorUsesMetaOAuth(
  settings: ContactCenterConnectorSettings,
): boolean {
  return (
    settings.connectorType === facebookMessengerConnectorType ||
    settings.connectorType === facebookCommentsConnectorType ||
    settings.connectorType === instagramConnectorType ||
    settings.connectorType === instagramCommentsConnectorType
  );
}

export function connectorUsesGmailOAuth(
  settings: ContactCenterConnectorSettings,
): boolean {
  return settings.connectorType === gmailConnectorType;
}

export function metaOAuthKindForChannelType(
  type: ContactCenterChannelType,
):
  | "facebook"
  | "facebook-comments"
  | "instagram"
  | "instagram-comments"
  | null {
  if (type === "FACEBOOK_MESSENGER") return "facebook";
  if (type === "FACEBOOK_COMMENTS") return "facebook-comments";
  if (type === "INSTAGRAM") return "instagram";
  if (type === "INSTAGRAM_COMMENTS") return "instagram-comments";
  return null;
}

function defaultConnectorTypeForChannel(
  channelType: ContactCenterChannelType | undefined,
): ContactCenterConnectorType {
  if (channelType === "FACEBOOK_MESSENGER")
    return facebookMessengerConnectorType;
  if (channelType === "FACEBOOK_COMMENTS") return facebookCommentsConnectorType;
  if (channelType === "INSTAGRAM") return instagramConnectorType;
  if (channelType === "INSTAGRAM_COMMENTS")
    return instagramCommentsConnectorType;
  if (channelType === tiktokBusinessConnectorType)
    return tiktokBusinessConnectorType;
  if (channelType === "EMAIL") return gmailConnectorType;
  if (channelType === telegramConnectorType) return telegramConnectorType;
  if (channelType === customWebhookConnectorType)
    return customWebhookConnectorType;
  return nativeDeviceConnectorType;
}

export function telegramConnectionVariant(
  value: unknown,
): TelegramConnectionVariant {
  return value === telegramBusinessConnectionVariant
    ? telegramBusinessConnectionVariant
    : telegramBotConnectionVariant;
}

export function connectorUsesTelegram(
  settings: ContactCenterConnectorSettings,
): boolean {
  return settings.connectorType === telegramConnectorType;
}

function compactSettings(
  settings: Record<string, unknown>,
): Record<string, unknown> {
  return Object.fromEntries(
    Object.entries(settings).filter(([, value]) => {
      if (value === null || value === undefined) return false;
      if (typeof value === "string") return value.trim().length > 0;
      if (Array.isArray(value)) return value.length > 0;
      return true;
    }),
  );
}

function normalizeHistorySyncState(
  value: unknown,
): ContactCenterHistorySyncStatus["status"] {
  return value === "IDLE" ||
    value === "SYNCING" ||
    value === "SUCCEEDED" ||
    value === "FAILED" ||
    value === "NOT_READY"
    ? value
    : "FAILED";
}

function stringSetting(
  settings: ConnectorSettingsRecord,
  key: string,
): string | null {
  const value = settings?.[key];
  return typeof value === "string" && value.length > 0 ? value : null;
}

function booleanSetting(
  settings: ConnectorSettingsRecord,
  key: string,
  fallback: boolean,
): boolean {
  const value = settings?.[key];
  return typeof value === "boolean" ? value : fallback;
}

function stringArraySetting(
  settings: ConnectorSettingsRecord,
  key: string,
): string[] | undefined {
  const value = settings?.[key];
  if (!Array.isArray(value)) return undefined;
  const strings = value.filter(
    (entry): entry is string =>
      typeof entry === "string" && entry.trim().length > 0,
  );
  return strings.length > 0 ? strings : undefined;
}

function telegramConnectionVariantSetting(
  settings: ConnectorSettingsRecord,
): TelegramConnectionVariant {
  return telegramConnectionVariant(settings?.telegramConnectionVariant);
}

function telegramBusinessConnectionSetting(
  settings: ConnectorSettingsRecord,
): ContactCenterConnectorSettings["telegramBusinessConnection"] {
  const value = settings?.telegramBusinessConnection;
  if (!value || typeof value !== "object" || Array.isArray(value))
    return undefined;
  const record = value as Record<string, unknown>;
  if (typeof record.id !== "string" || !record.id.trim()) return undefined;
  return {
    id: record.id,
    userChatId:
      typeof record.userChatId === "string" ? record.userChatId : null,
    userId: typeof record.userId === "string" ? record.userId : null,
    username: typeof record.username === "string" ? record.username : null,
    firstName: typeof record.firstName === "string" ? record.firstName : null,
    lastName: typeof record.lastName === "string" ? record.lastName : null,
    date: typeof record.date === "string" ? record.date : null,
    isEnabled: record.isEnabled !== false,
    canReply: record.canReply === true,
    lastUpdateId:
      typeof record.lastUpdateId === "number" ? record.lastUpdateId : null,
    checkedAt: typeof record.checkedAt === "string" ? record.checkedAt : null,
  };
}

function oauthProviderSetting(
  settings: ConnectorSettingsRecord,
): ContactCenterConnectorSettings["oauthProvider"] {
  const value = settings?.oauthProvider;
  return value === "facebook_login" || value === "instagram_login"
    ? value
    : null;
}

function pickupModeSetting(
  settings: ConnectorSettingsRecord,
): ContactCenterConnectorSettings["pickupMode"] {
  const value = settings?.pickupMode;
  return value === "ASSIGNED_ONLY" || value === "MANAGER_ASSIGN"
    ? value
    : "ANY_ELIGIBLE_EMPLOYEE";
}
